import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from '../Axiosapi';
import env_constants from '../Constants';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../Context/CartContext';

const stripePromise = loadStripe(env_constants.STRIPE_PUBLIC_KEY);

const CheckoutStripePayment = () => {
    const { cart, clearCart } = useCart();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const storedData = sessionStorage.getItem('stripeCheckoutData');
    const data = storedData ? JSON.parse(storedData) : {};
    const productimagenotfound = '/assets/images/product-image-not-found.webp';

    useEffect(() => {
        if (!cart || cart.length === 0) {
            navigate('/'); // Redirect to home if cart is empty
        }
    }, [cart, navigate]);

    const handlePaymentSuccess = async (paymentIntentId) => {
        try {
            const dataToSend = {
                ...data,
                payment_method: 'Stripe',
                stripePaymentMethodId: paymentIntentId,
                payment_intent_id: paymentIntentId,
            };

            setIsSubmitting(true);
            const response = await axios.post(`${env_constants.API_BASE_URL}/place-order`, dataToSend);

            if (response.status === 200) {
                const successMessage = response?.data?.message || 'Order placed successfully!';
                toast.success(successMessage);
                navigate('/thank-you');
                clearCart();
            } else {
                const errorMessage = response?.data?.message || 'Failed to place the order.';
                toast.error(errorMessage);
            }
        } catch (error) {
            const apiErrorMessage = error?.response?.data?.message || error?.message || 'Error placing the order. Please try again later.';
            toast.error(apiErrorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const safeToFixed = (value, decimals = 2) => {
        return value !== undefined ? value.toFixed(decimals) : '0.00';
    };

    return (
        <div className="container">
            <div className="cpp-box">
                <div className='d-flex justify-content-center'>
                    <img src="/auto-part.png" width="45%" alt="Auto Part Station logo" className="cpp-logo m-auto" />
                </div>
                <div className="cpp-title">
                    <h2>Complete Your Payments</h2>
                    <p>${safeToFixed(data.grand_total)}</p>
                </div>
                <Elements stripe={stripePromise}>
                    <StripeCheckoutForm
                        data={data}
                        totalAmount={safeToFixed(data.grand_total)}
                        onSuccess={handlePaymentSuccess}
                        isSubmitting={isSubmitting}
                    />
                </Elements>
                <div className="cpp-billing-address">
                    <h3>Billing Address</h3>
                    <p>Name: <span>{`${data.f_name || ''} ${data.l_name || ''}`}</span></p>
                    <p>Email: <span>{data.email || ''}</span></p>
                    <p>Phone: <span>{data.phone || ''}</span></p>
                    <p>Address: <span>{data.street_address || ''}</span></p>
                    <p>Area Detail: <span>{data.post_code || ''}, {data.state || ''}, {data.city || ''}</span></p>
                </div>
                <div className="cpp-items mt-4">
                    <h3>Product Items</h3>
                    <div className="table-wrapper">
                        <table width="100%">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Price</th>
                                    <th>Quantity</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cartItems.map((product) => {
                                    const priceMeta = product.product_meta.find(meta => meta.option === '_price');
                                    const price = priceMeta ? parseFloat(priceMeta.value) : 0;
                                    const totalPrice = price * (product.quantity || 0);

                                    return (
                                        <tr key={product.id}>
                                            <td>
                                                <div className="cpp-item-product">
                                                    <img src={`${env_constants.API_BASE_PUBLIC_URL}/${product.featured_image}`} onError={(e) => e.target.src = productimagenotfound} alt={product.title} />
                                                    <h5>{product.title}</h5>
                                                </div>
                                            </td>
                                            <td>${safeToFixed(price)}</td>
                                            <td>{product.quantity || 0}</td>
                                            <td>${safeToFixed(totalPrice)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="cpp-total mt-4">
                    <h3>Order Summary</h3>
                    <p>Subtotal: <span>${safeToFixed(data.subTotal)}</span></p>
                    <p>Shipping Cost: <span>${safeToFixed(data.delivery_charges)}</span></p>
                    <p>GST: <span>${safeToFixed(data.totalGst)}</span></p>
                    <div><p>Total: <span>${safeToFixed(data.grand_total)}</span></p></div>
                </div>
            </div>
        </div>
    );
};

const StripeCheckoutForm = ({ data, totalAmount, onSuccess, isSubmitting }) => {
    const stripe = useStripe();
    const elements = useElements();

    const cardOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
        hidePostalCode: true, // Disables the ZIP/postal code field
        disableLink: true, // Disables the autofill link
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) {
            return;
        }

        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: `${data.f_name} ${data.l_name}`,
                email: data.email,
            },
        });

        if (error) {
            toast.error(`Payment error: ${error.message}`);
        } else {
            onSuccess(paymentMethod.id);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <CardElement options={cardOptions} />
            <button className='Stripe-button-custom' type="submit" disabled={!stripe || isSubmitting}>
                {isSubmitting ? 'Processing...' : `Pay $${totalAmount}`}
            </button>
        </form>
    );
};

export default CheckoutStripePayment;